import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import ClinicGallery from '@/pages/home/components/ClinicGallery';
import Head from '@/components/base/Head';
import { useAdminImage } from '@/hooks/useAdminImage';

export default function AboutPage() {
  const { t } = useTranslation();

  const heroBg = useAdminImage(
    'about',
    'images.about.hero_bg',
    'https://readdy.ai/api/search-image?query=Aerial%20drone%20view%20of%20downtown%20Tirana%20Albania%20cityscape%2C%20Skanderbeg%20Square%20with%20national%20museum%20and%20historic%20clock%20tower%2C%20colorful%20painted%20apartment%20buildings%20in%20bright%20yellow%20green%20red%20orange%20facades%2C%20Dajti%20mountain%20looming%20behind%20city%2C%20dramatic%20sunset%20with%20orange%20pink%20sky%2C%20vibrant%20urban%20panorama%2C%20wide%20angle%20lens%20photography&width=1920&height=1080&seq=tirana-aerial-sunset-2026&orientation=landscape'
  );

  const doctorImg = useAdminImage(
    'about',
    'images.about.doctor_img',
    'https://public.readdy.ai/ai/img_res/edited_b1997be9b4a7cbac1b908e8aed469f36_4e381a70.jpg'
  );

  return (
    <>
      <Head
        title={t('seo.about.title')}
        metas={[
          { name: 'description', content: t('seo.about.description') },
          { name: 'keywords', content: t('seo.about.keywords') },
          { property: 'og:title', content: t('seo.about.title') },
          { property: 'og:description', content: t('seo.about.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/about' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.about.title') },
          { name: 'twitter:description', content: t('seo.about.description') },
          { name: 'last-modified', content: '2026-06-08' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/about' },
        ]}
      />

      <main>
        <Navbar />

        <section className="relative min-h-[420px] md:min-h-[520px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${heroBg})`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/55"></div>
          <div className="relative z-10 text-center px-6 w-full max-w-4xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs tracking-[0.2em] uppercase font-label border border-white/20">
              {t('about.tag')}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mt-6 leading-[1.1]">
              {t('about.heading_line1')}
              <br />
              {t('about.heading_line2')}
            </h1>
          </div>
        </section>

        <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="flex-1">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight">
                  {t('about.vision_title')}
                </h2>
                <p className="mt-5 text-sm md:text-base text-foreground-500 leading-relaxed font-body">
                  {t('about.description')}
                </p>
                <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed font-body">
                  {t('about.vision_para2')}
                </p>
                <Link
                  to="/team"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer no-underline whitespace-nowrap"
                >
                  {t('about.meet_team_link')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="flex-1 w-full">
                <div className="relative rounded-3xl bg-primary-800 p-8 md:p-10 overflow-hidden group">
                  <div className="absolute top-4 left-4">
                    <span className="text-xs text-primary-300/60 tracking-[0.15em] uppercase font-label">
                      {t('about.tag')}
                    </span>
                  </div>
                  <div className="absolute top-4 right-6 flex gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400/50"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400/50"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400/50"></span>
                  </div>
                  <div className="mt-8 md:mt-10">
                    <i className="ri-double-quotes-l text-3xl text-primary-400/40"></i>
                    <p className="mt-4 text-lg md:text-xl text-white/85 leading-relaxed font-body italic">
                      {t('about.quote')}
                    </p>
                  </div>
                  <div className="mt-8 md:mt-10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img
                          src={doctorImg}
                          alt={t('about.name')}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-white text-sm font-label font-semibold">{t('about.name')}</p>
                        <p className="text-primary-300/70 text-xs font-body mt-0.5">{t('about.position')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary-500/10 blur-3xl group-hover:bg-primary-500/20 transition-colors"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                {t('about.albania_title')}
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto font-body leading-relaxed">
                {t('about.albania_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-building-2-line',
                  title: t('about.albania_card1_title'),
                  desc: t('about.albania_card1_desc'),
                },
                {
                  icon: 'ri-landscape-line',
                  title: t('about.albania_card2_title'),
                  desc: t('about.albania_card2_desc'),
                },
                {
                  icon: 'ri-global-line',
                  title: t('about.albania_card3_title'),
                  desc: t('about.albania_card3_desc'),
                },
              ].map((item, idx) => (
                <div key={idx} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100/80">
                    <i className={`${item.icon} text-base text-accent-600`}></i>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed font-body">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicGallery />

        <section className="py-16 md:py-24 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              {t('about.experience_title')}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-500 font-body leading-relaxed">
              {t('about.experience_subtitle')}
            </p>
            <Link
              to="/booking"
              className="mt-7 inline-block px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm tracking-[0.1em] uppercase font-label hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
            >
              {t('booking.cta')}
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}